import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useContractWrite, usePrepareContractWrite, useContractRead, useAccount, useNetwork, useBalance } from 'wagmi';
import { NFT_CONTRACT_ABI, NFT_CONTRACT_ADDRESS } from '@/config/nftContract';
import { useToast } from '@/components/ui/use-toast';
import { formatEther, isAddress } from 'viem';
import { useAdmin } from '@/hooks/useAdmin';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle, ExternalLink } from 'lucide-react';

export function NFTContractManagement() {
  const { toast } = useToast();
  const { address } = useAccount();
  const { chain } = useNetwork();
  const { isAdmin } = useAdmin();
  const [newPlatformFee, setNewPlatformFee] = useState('');
  const [newTreasuryAddress, setNewTreasuryAddress] = useState('');
  const [newMintPrice, setNewMintPrice] = useState('');
  const [newOwnerAddress, setNewOwnerAddress] = useState('');
  const [isOwnerVerified, setIsOwnerVerified] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const chainId = chain?.id || 97; // Default to BSC testnet
  const nftContractAddress = NFT_CONTRACT_ADDRESS[chainId as keyof typeof NFT_CONTRACT_ADDRESS];

  console.log('Chain ID:', chainId);
  console.log('NFT Contract address:', nftContractAddress);

  // Get contract balance
  const { data: contractBalance, refetch: refetchBalance } = useBalance({
    address: nftContractAddress as `0x${string}`,
    watch: true,
    enabled: Boolean(nftContractAddress),
  });

  // Get current platform fee (in basis points)
  const { data: currentPlatformFee, refetch: refetchPlatformFee } = useContractRead({
    address: nftContractAddress as `0x${string}`,
    abi: NFT_CONTRACT_ABI,
    functionName: 'platformFee',
    watch: true,
    cacheTime: 0,
    enabled: Boolean(nftContractAddress),
    onError: (error) => {
      console.error('Error fetching platform fee:', error);
    }
  });

  // Get current treasury address
  const { data: currentTreasuryAddress, refetch: refetchTreasury } = useContractRead({
    address: nftContractAddress as `0x${string}`,
    abi: NFT_CONTRACT_ABI,
    functionName: 'treasuryAddress',
    watch: true,
    cacheTime: 0,
    enabled: Boolean(nftContractAddress),
    onError: (error) => {
      console.error('Error fetching treasury address:', error);
    }
  });

  // Get current mint price
  const { data: currentMintPrice, refetch: refetchMintPrice } = useContractRead({
    address: nftContractAddress as `0x${string}`,
    abi: NFT_CONTRACT_ABI,
    functionName: 'mintPrice',
    watch: true,
    cacheTime: 0,
    enabled: Boolean(nftContractAddress),
    onError: (error) => {
      console.error('Error fetching mint price:', error);
    }
  });

  // Get contract owner
  const { data: contractOwner, isSuccess: ownerFetchSuccess } = useContractRead({
    address: nftContractAddress as `0x${string}`,
    abi: NFT_CONTRACT_ABI,
    functionName: 'owner',
    watch: true,
    enabled: Boolean(nftContractAddress),
    onError: (error) => {
      console.error('Error fetching owner:', error);
    }
  });

  // Get collection info
  const { data: collectionInfo } = useContractRead({
    address: nftContractAddress as `0x${string}`,
    abi: NFT_CONTRACT_ABI,
    functionName: 'getCollectionInfo',
    watch: true,
    enabled: Boolean(nftContractAddress),
    onError: (error) => {
      console.error('Error fetching collection info:', error);
    }
  });

  // Verify if connected wallet is the owner
  useEffect(() => {
    if (address && contractOwner && ownerFetchSuccess) {
      const isOwner = address.toLowerCase() === (contractOwner as string).toLowerCase();
      setIsOwnerVerified(isOwner);
      
      if (!isOwner) {
        setValidationError('You are not the contract owner');
      } else {
        setValidationError(null);
      }
    }
  }, [address, contractOwner, ownerFetchSuccess]);

  // Prepare set platform fee (in basis points, e.g., 250 = 2.5%)
  const { config: setPlatformFeeConfig } = usePrepareContractWrite({
    address: nftContractAddress as `0x${string}`,
    abi: NFT_CONTRACT_ABI,
    functionName: 'setPlatformFee',
    args: [newPlatformFee ? BigInt(newPlatformFee) : 0n],
    enabled: Boolean(nftContractAddress && newPlatformFee && isOwnerVerified),
  });

  // Prepare set treasury address
  const { config: setTreasuryConfig } = usePrepareContractWrite({
    address: nftContractAddress as `0x${string}`,
    abi: NFT_CONTRACT_ABI,
    functionName: 'setTreasuryAddress',
    args: [newTreasuryAddress as `0x${string}`],
    enabled: Boolean(nftContractAddress && newTreasuryAddress && isOwnerVerified && isAddress(newTreasuryAddress)),
  });

  // Prepare set mint price
  const { config: setMintPriceConfig } = usePrepareContractWrite({
    address: nftContractAddress as `0x${string}`,
    abi: NFT_CONTRACT_ABI,
    functionName: 'setMintPrice',
    args: [newMintPrice ? BigInt(parseFloat(newMintPrice) * 1e18) : 0n],
    enabled: Boolean(nftContractAddress && newMintPrice && isOwnerVerified),
  });

  // Prepare transfer ownership
  const { config: transferOwnershipConfig } = usePrepareContractWrite({
    address: nftContractAddress as `0x${string}`,
    abi: NFT_CONTRACT_ABI,
    functionName: 'transferOwnership',
    args: [newOwnerAddress as `0x${string}`],
    enabled: Boolean(nftContractAddress && newOwnerAddress && isOwnerVerified),
  });

  // Write functions
  const { write: writePlatformFee, isLoading: isSettingFee } = useContractWrite({
    ...setPlatformFeeConfig,
    onSuccess: async () => {
      toast({
        title: "Success",
        description: "Platform fee updated successfully",
      });
      setNewPlatformFee('');
      setTimeout(() => refetchPlatformFee(), 2000);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const { write: writeTreasuryAddress, isLoading: isSettingTreasury } = useContractWrite({
    ...setTreasuryConfig,
    onSuccess: async () => {
      toast({
        title: "Success",
        description: "Treasury address updated successfully",
      });
      setNewTreasuryAddress('');
      setTimeout(() => refetchTreasury(), 2000);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const { write: writeMintPrice, isLoading: isSettingMintPrice } = useContractWrite({
    ...setMintPriceConfig,
    onSuccess: async () => {
      toast({
        title: "Success",
        description: "Mint price updated successfully",
      });
      setNewMintPrice('');
      setTimeout(() => refetchMintPrice(), 2000);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const { write: writeTransferOwnership, isLoading: isTransferringOwnership } = useContractWrite({
    ...transferOwnershipConfig,
    onSuccess: async () => {
      toast({
        title: "Success",
        description: "Ownership transfer initiated. New owner must accept.",
      });
      setNewOwnerAddress('');
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Withdraw funds
  const { config: withdrawConfig } = usePrepareContractWrite({
    address: nftContractAddress as `0x${string}`,
    abi: NFT_CONTRACT_ABI,
    functionName: 'withdraw',
    enabled: Boolean(nftContractAddress && isOwnerVerified),
  });

  const { write: writeWithdraw, isLoading: isWithdrawing } = useContractWrite({
    ...withdrawConfig,
    onSuccess: async () => {
      toast({
        title: "Success!",
        description: "Funds withdrawn successfully to your wallet",
      });
      // Refresh balance after withdrawal
      setTimeout(() => refetchBalance(), 2000);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const handleWithdraw = useCallback(() => {
    if (!isOwnerVerified) {
      toast({
        title: "Error",
        description: "You must be the contract owner to withdraw funds",
        variant: "destructive",
      });
      return;
    }
    if (window.confirm('Are you sure you want to withdraw all funds from the contract?')) {
      writeWithdraw?.();
    }
  }, [writeWithdraw, isOwnerVerified, toast]);

  const handlePlatformFeeUpdate = useCallback(() => {
    if (!isOwnerVerified) {
      toast({
        title: "Error",
        description: "You must be the contract owner to update the platform fee",
        variant: "destructive",
      });
      return;
    }
    writePlatformFee?.();
  }, [writePlatformFee, isOwnerVerified, toast]);

  const handleTreasuryUpdate = useCallback(() => {
    if (!isOwnerVerified) {
      toast({
        title: "Error",
        description: "You must be the contract owner to update the treasury address",
        variant: "destructive",
      });
      return;
    }
    writeTreasuryAddress?.();
  }, [writeTreasuryAddress, isOwnerVerified, toast]);

  const handleMintPriceUpdate = useCallback(() => {
    if (!isOwnerVerified) {
      toast({
        title: "Error",
        description: "You must be the contract owner to update the mint price",
        variant: "destructive",
      });
      return;
    }
    writeMintPrice?.();
  }, [writeMintPrice, isOwnerVerified, toast]);

  const handleOwnershipTransfer = useCallback(() => {
    if (!isOwnerVerified) {
      toast({
        title: "Error",
        description: "You must be the contract owner to transfer ownership",
        variant: "destructive",
      });
      return;
    }
    writeTransferOwnership?.();
  }, [writeTransferOwnership, isOwnerVerified, toast]);

  const getNativeCurrency = () => {
    switch (chain?.id) {
      case 56:
      case 97:
        return 'BNB';
      case 1:
      case 11155111:
        return 'ETH';
      case 43114:
      case 43113:
        return 'AVAX';
      default:
        return 'Native Token';
    }
  };

  const getExplorerUrl = () => {
    const baseUrl = chain?.id === 56 ? 'bscscan.com' :
                  chain?.id === 97 ? 'testnet.bscscan.com' :
                  chain?.id === 1 ? 'etherscan.io' :
                  chain?.id === 11155111 ? 'sepolia.etherscan.io' :
                  chain?.id === 43114 ? 'snowtrace.io' :
                  chain?.id === 43113 ? 'testnet.snowtrace.io' :
                  '';
    return baseUrl;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>NFT Contract Management</CardTitle>
        <CardDescription>
          Manage NFT contract settings
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Network Status */}
        <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
          <div>
            <p className="text-sm font-medium">Network Status</p>
            <p className="text-xs text-muted-foreground">Chain ID: {chainId}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium">Connected Account: {address?.slice(0, 6)}...{address?.slice(-4)}</p>
            <p className="text-xs text-muted-foreground">Owner Status: {isOwnerVerified ? 'Owner' : 'Not Owner'}</p>
          </div>
        </div>

        {validationError && (
          <div className="p-4 bg-destructive/10 border border-destructive rounded-lg">
            <p className="text-sm text-destructive">{validationError}</p>
          </div>
        )}

        {/* Contract Address */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Contract Address</h3>
          <div className="space-y-2">
            <Label>NFT Contract</Label>
            <div className="flex items-center gap-2">
              <Input
                value={nftContractAddress || "Not available"}
                readOnly
                className="bg-muted font-mono text-sm"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  window.open(`https://${getExplorerUrl()}/address/${nftContractAddress}`, '_blank');
                }}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Collection Info */}
        {collectionInfo && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Collection Info</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={collectionInfo[0] || "Loading..."}
                  readOnly
                  className="bg-muted text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Symbol</Label>
                <Input
                  value={collectionInfo[1] || "Loading..."}
                  readOnly
                  className="bg-muted text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Total Supply</Label>
                <Input
                  value={collectionInfo[5]?.toString() || "0"}
                  readOnly
                  className="bg-muted text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Max Supply</Label>
                <Input
                  value={collectionInfo[6]?.toString() || "0"}
                  readOnly
                  className="bg-muted text-sm"
                />
              </div>
            </div>
          </div>
        )}

        {/* Platform Fee Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Platform Fee</h3>
          
          {/* Current Fee */}
          <div className="space-y-2">
            <Label>Current Fee (Basis Points)</Label>
            <div className="flex items-center gap-2">
              <Input
                value={currentPlatformFee ? `${currentPlatformFee.toString()} (${(Number(currentPlatformFee) / 100).toFixed(2)}%)` : "Loading..."}
                readOnly
                className="bg-muted font-mono text-sm"
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => refetchPlatformFee()}
                    >
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Click to refresh current fee. 100 basis points = 1%</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* New Fee */}
          <div className="space-y-2">
            <Label>New Fee (Basis Points)</Label>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Enter new platform fee (e.g., 250 for 2.5%)"
                value={newPlatformFee}
                onChange={(e) => setNewPlatformFee(e.target.value)}
                className="font-mono text-sm"
              />
              <Button 
                onClick={handlePlatformFeeUpdate}
                disabled={isSettingFee || !isOwnerVerified}
              >
                {isSettingFee ? "Updating..." : "Update"}
              </Button>
            </div>
          </div>
        </div>

        {/* Treasury Address Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Treasury Address</h3>
          
          {/* Current Treasury */}
          <div className="space-y-2">
            <Label>Current Treasury</Label>
            <div className="flex items-center gap-2">
              <Input
                value={currentTreasuryAddress || "Loading..."}
                readOnly
                className="bg-muted font-mono text-sm"
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => refetchTreasury()}
                    >
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Click to refresh current treasury</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* New Treasury */}
          <div className="space-y-2">
            <Label>New Treasury Address</Label>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Enter new treasury address"
                value={newTreasuryAddress}
                onChange={(e) => setNewTreasuryAddress(e.target.value)}
                className="font-mono text-sm"
              />
              <Button 
                onClick={handleTreasuryUpdate}
                disabled={isSettingTreasury || !isOwnerVerified}
              >
                {isSettingTreasury ? "Updating..." : "Update"}
              </Button>
            </div>
          </div>
        </div>

        {/* Mint Price Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Mint Price</h3>
          
          {/* Current Mint Price */}
          <div className="space-y-2">
            <Label>Current Mint Price</Label>
            <div className="flex items-center gap-2">
              <Input
                value={currentMintPrice ? `${formatEther(currentMintPrice)} ${getNativeCurrency()}` : "Loading..."}
                readOnly
                className="bg-muted font-mono text-sm"
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => refetchMintPrice()}
                    >
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Click to refresh current mint price</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* New Mint Price */}
          <div className="space-y-2">
            <Label>New Mint Price ({getNativeCurrency()})</Label>
            <div className="flex items-center gap-2">
              <Input
                placeholder={`Enter new mint price in ${getNativeCurrency()}`}
                value={newMintPrice}
                onChange={(e) => setNewMintPrice(e.target.value)}
                className="font-mono text-sm"
              />
              <Button 
                onClick={handleMintPriceUpdate}
                disabled={isSettingMintPrice || !isOwnerVerified}
              >
                {isSettingMintPrice ? "Updating..." : "Update"}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>

      {/* Withdraw Funds */}
      <CardContent className="pt-6 border-t">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Withdraw Funds</h3>
          <p className="text-sm text-muted-foreground">
            Withdraw all accumulated TBNB/BNB from NFT mints to the owner's wallet.
          </p>
          
          {/* Available Balance */}
          <div className="space-y-2">
            <Label>Available Balance</Label>
            <div className="flex items-center gap-2">
              <Input
                value={contractBalance ? `${formatEther(contractBalance.value)} ${contractBalance.symbol}` : "Loading..."}
                readOnly
                className="bg-muted font-mono text-sm"
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => refetchBalance()}
                    >
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Click to refresh balance</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {contractBalance && Number(contractBalance.value) > 0 && (
              <p className="text-xs text-green-600 dark:text-green-400">
                💰 {formatEther(contractBalance.value)} {contractBalance.symbol} available to withdraw
              </p>
            )}
            {contractBalance && Number(contractBalance.value) === 0 && (
              <p className="text-xs text-muted-foreground">
                No funds available to withdraw
              </p>
            )}
          </div>
          
          <Button 
            onClick={handleWithdraw}
            disabled={!isOwnerVerified || isWithdrawing}
            variant="default"
            className="w-full"
          >
            {isWithdrawing ? "Withdrawing..." : "Withdraw All Funds"}
          </Button>
          
          {!isOwnerVerified && (
            <p className="text-xs text-destructive">
              Only the contract owner can withdraw funds
            </p>
          )}
        </div>
      </CardContent>

      {/* Ownership Management */}
      <CardContent className="pt-6 border-t">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Ownership Management</h3>
          
          {/* Current Owner */}
          <div className="space-y-2">
            <Label>Current Owner</Label>
            <div className="flex items-center gap-2">
              <Input
                value={contractOwner || "Loading..."}
                readOnly
                className="bg-muted font-mono text-sm"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  window.open(`https://${getExplorerUrl()}/address/${contractOwner}`, '_blank');
                }}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Transfer Ownership */}
          <div className="space-y-2">
            <Label>Transfer Ownership</Label>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Enter new owner address"
                value={newOwnerAddress}
                onChange={(e) => setNewOwnerAddress(e.target.value)}
                className="font-mono text-sm"
              />
              <Button 
                onClick={handleOwnershipTransfer}
                disabled={isTransferringOwnership || !isOwnerVerified}
                variant="destructive"
              >
                {isTransferringOwnership ? "Transferring..." : "Transfer"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Warning: Transferring ownership will remove your ability to manage this contract. Make sure the new owner address is correct.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
